import React, { useRef, useContext, useState, useEffect } from "react";
import "./CartItems.css";
import { ShopContext } from "../../Context/ShopContext";
import { CurrencyContext } from "../../Context/CurrencyContext";
import remove_icon from "../Assets/cart_cross_icon.webp";
import axios from "axios";
import useRazorpay from "react-razorpay";
import { FaCreditCard, FaPaypal } from "react-icons/fa"; // Importing icons
import { useNavigate } from "react-router-dom";
import Notification from "../Notification/Notification";

const CartItems = () => {
  const { getTotalCartAmount, cartItems, removeFromCart, fetchCartItems } =
    useContext(ShopContext);
  const { currency } = useContext(CurrencyContext);

  // Custom function to display cart prices without double conversion
  // Helper to round up to nearest 9
  function roundUpToNearest9(num) {
    num = Math.ceil(Number(num));
    if (num % 10 === 9) return num;
    return num + (9 - (num % 10));
  }

  const displayCartPrice = (price) => {
    let displayPrice = price;
    if (currency === "INR") {
      displayPrice = roundUpToNearest9(displayPrice);
    }
    return new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0
    }).format(displayPrice);
  };
  const [promoCode, setPromoCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [Razorpay] = useRazorpay();
  const [errorNotification, setErrorNotification] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(""); // State to track payment method
  const [showPaypalButton, setShowPaypalButton] = useState(false);
  // Shipping fee (used for Cash on Delivery) and COD selection state
  const [shippingFee, setShippingFee] = useState(0);
  const [codSelected, setCodSelected] = useState(false);

  // Whenever the COD checkbox or currency changes, adjust the extra fee
  useEffect(() => {
    if (currency === "INR" && codSelected) {
      setShippingFee(39);
    } else {
      setShippingFee(0);
    }
  }, [codSelected, currency]);

  // New state for form visibility and form data
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    delivery_address: "",
    name: "",
    country_code: "",
    mobile_number: "",
    pin_code: "",
    city: "",
    state: "",
    comment: "",
  });

  const [paypalError, setPaypalError] = useState(""); // State for PayPal errors

  const formRef = useRef(null);
  const handleContinueClick = () => {
    // First, show the form
    setShowForm(true);

    // Then scroll to the form after it's visible
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 100); // Add a small delay to ensure the form renders before scrolling
  };

  // Promo Code Handler
  const handlePromoCodeSubmit = async () => {
    const totalAmount = getTotalCartAmount();
    const token = localStorage.getItem("token");

    // Calculate total number of items in the cart
    const numberOfItems = cartItems.reduce(
      (total, item) => total + item.quantity,
      0
    );

    try {
      const response = await axios.post(
        "https://kraafty-backend.onrender.com/api/validate-promo-code/",
        {
          amount: totalAmount,
          promo_code: promoCode,
          no_of_products: numberOfItems, // <-- Include number_of_items in the request
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );

      if (response?.data?.amount !== undefined && response.data.message) {
        setDiscount(Number(response.data.amount) || 0); // Ensure discount is a valid number
        setMessage(response.data.message);
        setMessageType("success");
      } else {
        setMessage("Promo code not applied");
        setMessageType("error");
        setDiscount(0);
      }
    } catch (error) {
      setMessage("Promo code not applied");
      setMessageType("error");
      setDiscount(0);
    }
  };

  const [showNotification] = useState(false);

  const navigate = useNavigate();

  // Async function to verify payment
  const verify_payment = async (paymentDetails, paymentType) => {
    const totalAmount = getTotalCartAmount() - discount;
    const token = localStorage.getItem("token");
    console.log("Verifying payment with details:", {
      paymentDetails,
      paymentType,
      totalAmount,
      token: token ? "Token exists" : "No token found"
    });

    try {
      const cartId = localStorage.getItem("cartId");
      let payload;

      if (paymentType === "razorpay") {
        payload = {
          amount: totalAmount * 100,
          razorpay: true,
          razorpay_payment_id: paymentDetails.payment_id,
          razorpay_order_id: paymentDetails.order_id,
          razorpay_signature: paymentDetails.signature,
          cart_id: cartId
        };
      } else if (paymentType === "paypal") {
        payload = {
          amount: totalAmount * 100,
          razorpay: false,
          order_id: paymentDetails.order_id,  // Changed from paypal_order_id to order_id
          cart_id: cartId
        };
      }

      console.log("Final verification payload:", payload);

      const response = await axios.post(
        "https://kraafty-backend.onrender.com/api/verify-payment/",
        payload,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );

      console.log("Verification response:", response.data);

      if (response.data) {
        fetchCartItems();
        navigate("/");
      }
    } catch (error) {
      console.error("Payment verification failed:", error);
      console.error("Error response:", error.response?.data);
      setPaypalError(error.response?.data?.message || "Payment verification failed. Please try again.");
    }
  };

  const handleRazorpayPayment = async (order_id) => {
    // const totalAmount = getTotalCartAmount() - discount;
    try {
      const options = {
        key: "rzp_live_XwM2KzkjN8IqVB", // Razorpay test key
        // key: "rzp_live_liYPHRU3JwvCSw",
        name: "Kraafty",
        description: "Kraafty Secure Transaction",
        image: "https://www.kraafty.com/logo.webp",
        order_id: order_id,
        handler: function (response) {
          verify_payment(
            {
              payment_id: response.razorpay_payment_id,
              order_id: response.razorpay_order_id,
              signature: response.razorpay_signature,
            },
            "razorpay"
          );
        },
        prefill: {
          name: formData.name || "",
          email: "",
          contact: formData.mobile_number || "",
        },
        notes: {
          address: formData.delivery_address || "",
        },
        theme: {
          color: "#3399cc",
        },
      };

      const rzp1 = new Razorpay(options);

      rzp1.on("payment.failed", function (response) {
        console.error("Payment failed:", response.error);
      });

      rzp1.open();
    } catch (error) {
      console.error("Error during Razorpay checkout:", error);
    }
  };

  const handlePaypalPayment = async () => {
    const totalAmount = getTotalCartAmount() - discount;
    const token = localStorage.getItem("token");
    const cartId = localStorage.getItem("cartId");

    try {
      console.log("Creating order with data:", {
        amount: totalAmount,
        currency: currency,
        promo_code: promoCode || "Nil",
        razorpay: false,
        cart_id: cartId,
        ...formData,
      });

      // Helper to round up to nearest 9
      function roundUpToNearest9(num) {
        num = Math.ceil(Number(num));
        if (num % 10 === 9) return num;
        return num + (9 - (num % 10));
      }
      let apiAmount = totalAmount;
      if (currency === "INR") {
        apiAmount = roundUpToNearest9(apiAmount);
      }
      // Calculate formatted amount for display and PayPal
      const formattedAmount = apiAmount.toFixed(2);

      // First create order in our backend - cart prices should already be in correct currency
      // after our ShopContext modifications (INR for Indian customers)
      const response = await axios.post(
        "https://kraafty-backend.onrender.com/api/create-order/",
        {
          amount: apiAmount,
          currency: currency,
          promo_code: promoCode || "Nil",
          razorpay: false,
          cart_id: cartId,
          ...formData,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );

      console.log("Backend response:", response.data);

      if (!response.data || !response.data.id) {
        throw new Error("Failed to create order in backend");
      }

      setShowPaypalButton(true);

      // Initialize PayPal button
      window.paypal
        .Buttons({
          createOrder: (data, actions) => {
            return actions.order.create({
              purchase_units: [
                {
                  amount: {
                    value: formattedAmount,
                    currency_code: currency,
                    breakdown: {
                      item_total: {
                        value: formattedAmount,
                        currency_code: currency
                      }
                    }
                  },
                  items: cartItems.map(item => ({
                    name: item.product_name,
                    unit_amount: {
                      value: (currency === "INR" ? roundUpToNearest9(item.offer_price) : item.offer_price).toFixed(2),
                      currency_code: currency
                    },
                    quantity: item.quantity.toString()
                  }))
                },
              ],
            });
          },
          onApprove: async (data, actions) => {
            try {
              const details = await actions.order.capture();
              await verify_payment({ order_id: details.id }, "paypal");
            } catch (error) {
              console.error("Payment capture failed:", error);
              setPaypalError("Payment capture failed. Please try again.");
            }
          },
          onError: (err) => {
            console.error("PayPal error:", err);
            setPaypalError("Payment failed. Please try again or use a different payment method.");
          },
          onCancel: () => {
            setPaypalError("Payment was cancelled. Please try again if you wish to complete the purchase.");
          }
        })
        .render("#paypal-button-container");
    } catch (error) {
      console.error("Order creation error:", error);
      console.error("Error response:", error.response?.data);
      setPaypalError(error.response?.data?.message || "Error creating order. Please try again.");
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    const totalAmount = getTotalCartAmount() - discount;

    // const pinCodeRegex = /^[0-9]{2,10}$/;

    const showErrorNotification = (message) => {
      setErrorNotification(message);
      setTimeout(() => {
        setErrorNotification("");
      }, 5000);
    };

    if (
      !formData.name ||
      !formData.mobile_number ||
      !formData.delivery_address
    ) {
      showErrorNotification("Please fill in all required fields.");
      return;
    }

    // if (!pinCodeRegex.test(formData.pin_code)) {
    //   showErrorNotification("Invalid pin code. Please enter a valid pin code.");
    //   return;
    // }

    // Ensure payment method is selected
    if (!paymentMethod) {
      showErrorNotification("Please select a payment method.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const cartId = localStorage.getItem("cartId");
      const res = await axios.post(
        "https://kraafty-backend.onrender.com/api/create-order/",
        {
          // Cart prices are already in the correct currency from our ShopContext modifications
          // so no need to do additional conversion here
          amount: totalAmount * 100, // multiplied by 100 for payment processors like Razorpay
          currency: currency,
          promo_code: promoCode || "Nil",
          cart_id: cartId,
          ...formData,
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );

      const order_id = res.data.data.id;

      if (paymentMethod === "razorpay") {
        handleRazorpayPayment(order_id);
      } else if (paymentMethod === "paypal") {
        handlePaypalPayment();
      }
    } catch (error) {
      setErrorNotification("An unexpected error occurred. Please try again.");
      setTimeout(() => {
        setErrorNotification("");
      }, 5000);
    }
  };

  const finalAmount = (
    Number(getTotalCartAmount()) -
    Number(discount) +
    Number(shippingFee)
  ).toFixed(2); // shippingFee already accounts for COD charges if selected

  // const sizeMap = {
  //   S: "100 gm",
  //   M: "250 gm",
  //   L: "500 gm",
  //   XL: "1 Kg",
  // };

  return (
    <div className="cartitems">
      {/* <div className="cartitems-format-main">
        <p>Products</p>
        <p>Title</p>
        <p>Price</p>
        <p>Size</p>
        <p>Quantity</p>
        <p>Total</p>
        <p>Remove</p>
      </div> */}
      <hr />
      <Notification
        message={message}
        isVisible={message}
        onClose={() => setMessage(false)}
      />
      {cartItems.length > 0 ? (
        cartItems.map((item) => (
          <div key={item.id}>
            <div className="cartitems-format cartitems-format-main">
              <img
                src={item.image}
                alt={item.product_name}
                className="carticon-product-icon bg-purple-500 min-w-24"
                loading="lazy"
              />
              <div className="name-and-data">
                <div className="cartitem-name">
                  {item.product_name}
                  {/* {(() => {
                  const words = item.product_name.split(' ');
                  return words.length > 10
                  ? `${words.slice(0, 10).join(' ')}...`
                  : item.product_name;
                  })()} */}
                </div>

                <div className="cartitems-details">
                  {/* <p>${item.offer_price}</p> */}
                  <div className="size">Size: {item.size}</div>
                  <button className="cartitems-quantity">
                    Qty: {item.quantity}
                  </button>
                </div>

                {/*  price of the product */}
                <div className="total-price">
                  {displayCartPrice(item.offer_price * item.quantity)}
                </div>
              </div>
              <img
                className="cartitems-remove-icon"
                src={remove_icon}
                onClick={() =>
                  removeFromCart(item.id, item.size, item.offer_price, item.mrp)
                }
                alt="Remove"
              />
            </div>

            <hr />
          </div>
        ))
      ) : (
        <p>No items in the cart.</p>
      )}
      <div className="cartitems-down">
        <div className="cartitems-promocode">
          <p>If you have a promo code, enter it here</p>
          <div className="cartitems-promobox">
            <input
              type="text"
              placeholder="Promo code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
            />
            <button onClick={handlePromoCodeSubmit}>Submit</button>
          </div>
          {message && (
            <p
              className={`cartitems-message ${
                messageType === "success" ? "success" : "error"
              }`}
            >
              {message}
            </p>
          )}
        </div>

        <div className="cartitems-total">
          <div className="cart-summary-title">Cart Totals</div>
          <div>
            <div className="cartitems-total-item">
              <p>Subtotal</p>
              <p>{displayCartPrice(getTotalCartAmount().toFixed(2))}</p>
            </div>
            <hr />
            <div className="cartitems-total-item">
              <p>Shipping Fee</p>
              <p>
                {shippingFee === 0
                  ? "Free"
                  : displayCartPrice(shippingFee)}
              </p>
            </div>
            <hr />
            {discount > 0 && (
              <div className="cartitems-total-item">
                <p>Discount</p>
                <p>{displayCartPrice(discount)}</p>
              </div>
            )}
            <hr />
            <div className="cartitems-total-item">
              <h3>Total</h3>
              <h3>{displayCartPrice(finalAmount)}</h3>
            </div>
          </div>
          {currency === "INR" && (
            <div className="cartitems-cod-option" style={{ marginTop: "10px" }}>
              <input
                type="checkbox"
                id="cod"
                checked={codSelected}
                onChange={(e) => setCodSelected(e.target.checked)}
              />
              <label htmlFor="cod" style={{ marginLeft: "8px" }}>
                Cash on Delivery
              </label>
            </div>
          )}
          <button onClick={handleContinueClick}>CLICK TO CONTINUE</button>
        </div>
      </div>

      {showForm && (
        <div
          ref={formRef}
          className="checkout-form w-full max-w-md mx-auto p-6 bg-white shadow-lg rounded-lg"
        >
          <h2>Fill the Delivery Information</h2>
          <form onSubmit={handleFormSubmit}>
            <input
              type="text"
              placeholder="Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="Delivery Address"
              value={formData.delivery_address}
              onChange={(e) =>
                setFormData({ ...formData, delivery_address: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="Country"
              value={formData.country_code}
              onChange={(e) =>
                setFormData({ ...formData, country_code: e.target.value })
              }
            />
            <input
              type="text"
              placeholder="Mobile Number"
              value={formData.mobile_number}
              onChange={(e) =>
                setFormData({ ...formData, mobile_number: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="Pin Code"
              value={formData.pin_code}
              onChange={(e) =>
                setFormData({ ...formData, pin_code: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="City"
              value={formData.city}
              onChange={(e) =>
                setFormData({ ...formData, city: e.target.value })
              }
              required
            />
            <input
              type="text"
              placeholder="State"
              value={formData.state}
              onChange={(e) =>
                setFormData({ ...formData, state: e.target.value })
              }
              required
            />
            <textarea
              placeholder="Comments (optional)"
              value={formData.comment}
              onChange={(e) =>
                setFormData({ ...formData, comment: e.target.value })
              }
            />

            {/* Payment Options */}
            <div className="payment-buttons mx-2">
              {!paymentMethod && (
                <div className="flex justify-around mt-4">
                  <div
                    className={`cursor-pointer text-center p-4 border rounded-lg ${
                      paymentMethod === "razorpay"
                        ? "border-blue-500"
                        : "border-gray-500"
                    }`}
                    onClick={() => setPaymentMethod("razorpay")}
                  >
                    <FaCreditCard
                      size={50}
                      color={paymentMethod === "razorpay" ? "blue" : "gray"}
                    />
                    <p className="mt-2">Pay with Razorpay</p>
                  </div>

                  <div
                    className={`cursor-pointer text-center p-4 border rounded-lg ${
                      paymentMethod === "paypal"
                        ? "border-blue-500"
                        : "border-gray-500"
                    }`}
                    onClick={() => {
                      setPaymentMethod("paypal");
                      if (typeof setShowPaypalButton === "function") {
                        setShowPaypalButton(true);
                      }
                    }}
                  >
                    <FaPaypal
                      size={50}
                      color={paymentMethod === "paypal" ? "blue" : "gray"}
                    />
                    <p className="mt-2">Pay with PayPal</p>
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button className="mt-4" type="submit">
              Submit
            </button>
          </form>
        </div>
      )}

      {showNotification && (
        <div className="fixed top-24 right-8 bg-green-500 text-white p-4 rounded-lg shadow-lg">
          Your Order Placed successfully, You get invoice over Mail/Whatsapp.
        </div>
      )}
      {errorNotification && (
        <div className="fixed top-28 left-4 w-full p-4 bg-red-500 text-white text-center">
          <p>{errorNotification}</p>
        </div>
      )}
      {paypalError && (
        <div className="fixed top-28 left-4 w-full p-4 bg-red-500 text-white text-center">
          <p>{paypalError}</p>
        </div>
      )}

      {paymentMethod === "paypal" && showPaypalButton && (
        <div id="paypal-button-container"></div>
      )}
    </div>
  );
};

export default CartItems;
